import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AchievementsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: 'Our Achievements & Recognition',
      subtitle: 'Building a Legacy of Legal Excellence',
      achievements: [
        {
          icon: 'Trophy',
          title: 'Legal Excellence Award',
          year: '2023',
          description: 'Recognized by the Kenya Law Society for outstanding contribution to legal practice and client service excellence.',
          category: 'Professional Recognition'
        },
        {
          icon: 'Star',
          title: 'Top Rated Law Firm',
          year: '2022-2023',
          description: 'Consistently rated among the top 10 law firms in Kenya for client satisfaction and case success rates.',
          category: 'Client Satisfaction'
        },
        {
          icon: 'Users',
          title: 'Community Impact Award',
          year: '2022',
          description: 'Honored for pro bono work and community legal education programs benefiting over 1,000 individuals.',
          category: 'Community Service'
        },
        {
          icon: 'BookOpen',
          title: 'Legal Innovation Recognition',
          year: '2021',
          description: 'Acknowledged for implementing innovative legal technology solutions and modern client service approaches.',
          category: 'Innovation'
        }
      ],
      notableCases: [
        {
          title: 'Corporate Merger Success',
          description: 'Successfully facilitated a KSh 2.5 billion corporate merger, ensuring regulatory compliance and stakeholder satisfaction.',
          impact: 'KSh 2.5B Transaction Value',
          year: '2023'
        },
        {
          title: 'Property Rights Victory',
          description: 'Won landmark property rights case, setting precedent for land ownership disputes in Kenya.',
          impact: '500+ Families Benefited',
          year: '2022'
        },
        {
          title: 'Employment Law Reform',
          description: 'Contributed to significant employment law reforms through strategic litigation and policy advocacy.',
          impact: 'National Policy Impact',
          year: '2021'
        }
      ],
      metrics: [
        { number: '500+', label: 'Cases Won', icon: 'CheckCircle' },
        { number: '98%', label: 'Success Rate', icon: 'TrendingUp' },
        { number: '1000+', label: 'Clients Served', icon: 'Users' },
        { number: '10+', label: 'Years Experience', icon: 'Calendar' }
      ]
    },
    sw: {
      title: 'Mafanikio na Utambuzi Wetu',
      subtitle: 'Kujenga Urithi wa Ubora wa Kisheria',
      achievements: [
        {
          icon: 'Trophy',
          title: 'Tuzo ya Ubora wa Kisheria',
          year: '2023',
          description: 'Ilitambuliwa na Chama cha Sheria cha Kenya kwa mchango mkuu katika mazoezi ya kisheria na ubora wa huduma kwa wateja.',
          category: 'Utambuzi wa Kitaalamu'
        },
        {
          icon: 'Star',
          title: 'Kampuni ya Sheria ya Hali ya Juu',
          year: '2022-2023',
          description: 'Imekuwa ikihesabiwa kati ya makampuni 10 ya juu ya sheria nchini Kenya kwa kuridhisha wateja na viwango vya mafanikio ya kesi.',
          category: 'Kuridhisha Wateja'
        },
        {
          icon: 'Users',
          title: 'Tuzo ya Athari ya Jamii',
          year: '2022',
          description: 'Iliheshimiwa kwa kazi ya pro bono na mipango ya elimu ya kisheria ya jamii inayofaidisha zaidi ya watu 1,000.',
          category: 'Huduma ya Jamii'
        },
        {
          icon: 'BookOpen',
          title: 'Utambuzi wa Ubunifu wa Kisheria',
          year: '2021',
          description: 'Ilitambuliwa kwa kutekeleza suluhisho za teknolojia za kisheria za kibunifu na mbinu za kisasa za huduma kwa wateja.',
          category: 'Ubunifu'
        }
      ],
      notableCases: [
        {
          title: 'Mafanikio ya Muungano wa Makampuni',
          description: 'Ilifanikiwa kuwezesha muungano wa makampuni wa KSh bilioni 2.5, ikihakikisha kufuata kanuni na kuridhisha wadau.',
          impact: 'Thamani ya Muamala KSh 2.5B',
          year: '2023'
        },
        {
          title: 'Ushindi wa Haki za Mali',
          description: 'Ilishinda kesi muhimu ya haki za mali, ikiweka mfano wa migogoro ya umiliki wa ardhi nchini Kenya.',
          impact: 'Familia 500+ Zilifaidika',
          year: '2022'
        },
        {
          title: 'Mageuzi ya Sheria ya Ajira',
          description: 'Ilichangia mageuzi muhimu ya sheria ya ajira kupitia mashtaka ya kimkakati na utetezi wa sera.',
          impact: 'Athari ya Sera ya Kitaifa',
          year: '2021'
        }
      ],
      metrics: [
        { number: '500+', label: 'Kesi Zilizoshindwa', icon: 'CheckCircle' },
        { number: '98%', label: 'Kiwango cha Mafanikio', icon: 'TrendingUp' },
        { number: '1000+', label: 'Wateja Waliohudumika', icon: 'Users' },
        { number: '10+', label: 'Miaka ya Uzoefu', icon: 'Calendar' }
      ]
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-xl font-heading font-semibold text-primary">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {currentContent.metrics.map((metric, index) => (
            <div key={index} className="card p-6 text-center space-y-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mx-auto">
                <Icon name={metric.icon} size={24} color="white" />
              </div>
              <div className="text-3xl font-heading font-bold text-primary">
                {metric.number}
              </div>
              <div className="text-sm font-caption text-text-secondary">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        {/* Achievements */}
        <div className="space-y-12 mb-16">
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary text-center">
            {currentLanguage === 'en' ? 'Awards & Recognition' : 'Tuzo na Utambuzi'}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {currentContent.achievements.map((achievement, index) => (
              <div key={index} className="card p-6 space-y-4 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={achievement.icon} size={24} color="white" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-heading font-semibold text-text-primary">
                        {achievement.title}
                      </h4>
                      <span className="text-sm font-caption text-primary font-semibold">
                        {achievement.year}
                      </span>
                    </div>
                    <div className="text-xs font-caption text-accent uppercase tracking-wide">
                      {achievement.category}
                    </div>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {achievement.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notable Cases */}
        <div className="space-y-12">
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary text-center">
            {currentLanguage === 'en' ? 'Notable Cases & Impact' : 'Kesi Muhimu na Athari'}
          </h3>
          
          <div className="space-y-8">
            {currentContent.notableCases.map((case_, index) => (
              <div key={index} className="card p-8 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-xl font-heading font-semibold text-text-primary">
                        {case_.title}
                      </h4>
                      <span className="text-sm font-caption text-primary font-semibold bg-surface/20 px-2 py-1 rounded">
                        {case_.year}
                      </span>
                    </div>
                    <p className="text-text-secondary leading-relaxed">
                      {case_.description}
                    </p>
                  </div>
                  <div className="lg:ml-8 lg:text-right">
                    <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary to-accent text-white px-4 py-2 rounded-lg">
                      <Icon name="TrendingUp" size={16} />
                      <span className="font-semibold text-sm">{case_.impact}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementsSection;